import { 
  UsageData, 
  ModelUsage, 
  TeamUsage, 
  ApiKeyUsage, 
  CostPrediction, 
  BillingInfo, 
  Discount, 
  DashboardStats,
  ChartDataPoint
} from '../types';

// Generate mock usage data for the last 30 days
export const generateMockUsageData = (days: number = 30): UsageData[] => {
  const data: UsageData[] = [];
  const models = [
    'anthropic.claude-3-5-sonnet-20241022-v2:0',
    'anthropic.claude-3-5-haiku-20241022-v1:0',
    'anthropic.claude-3-opus-20240229-v1:0',
    'anthropic.claude-3-sonnet-20240229-v1:0',
    'anthropic.claude-3-haiku-20240307-v1:0',
    'amazon.titan-text-premier-v1:0',
    'cohere.command-r-plus-v1:0',
    'meta.llama3-2-90b-instruct-v1:0'
  ];
  const teams = ['Engineering', 'Marketing', 'Sales', 'Support', 'Product'];
  const apiKeys = ['key-001', 'key-002', 'key-003', 'key-004', 'key-005'];
  
  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    
    // Generate 10-50 entries per day
    const entriesPerDay = Math.floor(Math.random() * 40) + 10;
    
    for (let j = 0; j < entriesPerDay; j++) {
      const model = models[Math.floor(Math.random() * models.length)];
      const team = teams[Math.floor(Math.random() * teams.length)];
      const apiKey = apiKeys[Math.floor(Math.random() * apiKeys.length)];
      
      // Different cost structures per model (AWS Bedrock pricing)
      const baseCost = model.includes('claude-3-opus') ? 0.015 :
                     model.includes('claude-3-5-sonnet') ? 0.003 :
                     model.includes('claude-3-sonnet') ? 0.003 :
                     model.includes('claude-3-haiku') || model.includes('claude-3-5-haiku') ? 0.00025 :
                     model.includes('titan-text-premier') ? 0.0005 :
                     model.includes('command-r-plus') ? 0.003 :
                     model.includes('llama3-2-90b') ? 0.002 : 0.001;
      
      const tokens = Math.floor(Math.random() * 2000) + 100;
      const cost = (tokens / 1000) * baseCost;
      
      data.push({
        timestamp: new Date(date.getTime() + Math.random() * 24 * 60 * 60 * 1000).toISOString(),
        requests: 1,
        tokens,
        cost: Math.round(cost * 100) / 100,
        model,
        team,
        apiKey,
        userId: `user-${Math.floor(Math.random() * 20) + 1}`
      });
    }
  }
  
  return data.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

// Mock data for different dashboard components
export const mockUsageData = generateMockUsageData();

export const mockModelUsage: ModelUsage[] = [
  { model: 'anthropic.claude-3-5-sonnet-20241022-v2:0', requests: 1450, tokens: 950000, cost: 28.50, percentage: 38 },
  { model: 'anthropic.claude-3-5-haiku-20241022-v1:0', requests: 2800, tokens: 1800000, cost: 4.50, percentage: 25 },
  { model: 'anthropic.claude-3-opus-20240229-v1:0', requests: 580, tokens: 380000, cost: 15.20, percentage: 18 },
  { model: 'amazon.titan-text-premier-v1:0', requests: 950, tokens: 680000, cost: 3.40, percentage: 12 },
  { model: 'cohere.command-r-plus-v1:0', requests: 420, tokens: 290000, cost: 8.70, percentage: 7 }
];

export const mockTeamUsage: TeamUsage[] = [
  { team: 'Engineering', requests: 2800, tokens: 1800000, cost: 32.50, users: 12 },
  { team: 'Product', requests: 1200, tokens: 750000, cost: 15.80, users: 8 },
  { team: 'Marketing', requests: 890, tokens: 520000, cost: 9.20, users: 6 },
  { team: 'Sales', requests: 650, tokens: 380000, cost: 7.40, users: 5 },
  { team: 'Support', requests: 310, tokens: 180000, cost: 3.60, users: 3 }
];

export const mockApiKeyUsage: ApiKeyUsage[] = [
  {
    apiKey: 'key-001',
    keyName: 'Production API',
    requests: 3200,
    tokens: 2100000,
    cost: 42.50,
    lastUsed: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    status: 'active'
  },
  {
    apiKey: 'key-002', 
    keyName: 'Development API',
    requests: 1850,
    tokens: 950000,
    cost: 18.20,
    lastUsed: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    status: 'active'
  },
  {
    apiKey: 'key-003',
    keyName: 'Testing API',
    requests: 750,
    tokens: 420000,
    cost: 6.80,
    lastUsed: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    status: 'active'
  },
  {
    apiKey: 'key-004',
    keyName: 'Staging API',
    requests: 120,
    tokens: 85000,
    cost: 2.15,
    lastUsed: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'suspended'
  }
];

export const mockCostPrediction: CostPrediction = {
  currentMonthCost: 287.45,
  predictedEndOfMonthCost: 425.80,
  dailyAverage: 12.60,
  daysRemaining: 11,
  trend: 'increasing'
};

export const mockBillingInfo: BillingInfo = {
  currentBalance: 1250.00,
  monthlyLimit: 2000.00,
  billingCycle: 'monthly',
  paymentMethod: '**** 4521',
  nextBillingDate: '2025-10-01',
  invoices: [
    {
      id: 'INV-2025-09',
      date: '2025-09-01',
      amount: 387.50,
      status: 'paid',
      downloadUrl: '#'
    },
    {
      id: 'INV-2025-08',
      date: '2025-08-01', 
      amount: 425.20,
      status: 'paid',
      downloadUrl: '#'
    },
    {
      id: 'INV-2025-07',
      date: '2025-07-01',
      amount: 298.75,
      status: 'paid',
      downloadUrl: '#'
    }
  ]
};

export const mockDiscounts: Discount[] = [
  {
    id: 'disc-001',
    name: 'Early Adopter Discount',
    type: 'percentage',
    value: 15,
    description: '15% off all API calls for early adopters',
    validFrom: '2025-01-01',
    validTo: '2025-12-31',
    isActive: true
  },
  {
    id: 'disc-002',
    name: 'Volume Discount',
    type: 'percentage',
    value: 10,
    description: '10% off when monthly usage exceeds $500',
    validFrom: '2025-01-01',
    validTo: '2025-12-31',
    isActive: true
  }
];

export const mockDashboardStats: DashboardStats = {
  totalRequests: 5850,
  totalTokens: 3630000,
  totalCost: 287.45,
  activeKeys: 3,
  topModels: mockModelUsage,
  recentActivity: mockUsageData.slice(-10),
  costPrediction: mockCostPrediction
};

// Generate chart data for different time ranges
export const generateChartData = (days: number): ChartDataPoint[] => {
  const data: ChartDataPoint[] = [];
  
  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    
    // Aggregate daily data
    const dayData = mockUsageData.filter(item => {
      const itemDate = new Date(item.timestamp);
      return itemDate.toDateString() === date.toDateString();
    });
    
    const requests = dayData.length;
    const tokens = dayData.reduce((sum, item) => sum + item.tokens, 0);
    const cost = dayData.reduce((sum, item) => sum + item.cost, 0);
    
    data.push({
      date: date.toISOString().split('T')[0],
      requests,
      tokens,
      cost: Math.round(cost * 100) / 100
    });
  }
  
  return data;
};